import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';

const GROUP_COLORS = [
  { bg: '#e3f2fd', border: '#2196f3', text: '#1565c0' },
  { bg: '#e8f5e9', border: '#4caf50', text: '#2e7d32' },
  { bg: '#fff3e0', border: '#ff9800', text: '#e65100' },
  { bg: '#f3e5f5', border: '#9c27b0', text: '#6a1b9a' },
  { bg: '#e0f7fa', border: '#00bcd4', text: '#00838f' },
  { bg: '#fce4ec', border: '#e91e63', text: '#c2185b' },
  { bg: '#fff8e1', border: '#ffc107', text: '#ff8f00' },
  { bg: '#e8eaf6', border: '#3f51b5', text: '#283593' },
  { bg: '#efebe9', border: '#795548', text: '#4e342e' },
  { bg: '#eceff1', border: '#607d8b', text: '#37474f' },
];

const getGroupColor = (index) => GROUP_COLORS[index % GROUP_COLORS.length];

const StageGroupSelect = ({ groupName, groups = [], isFilterActive, onFilter }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0, openUpward: false });
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const isMouseDownOnDropdown = useRef(false);

  const isInteractive = typeof onFilter === 'function';

  const sortedGroups = [...groups].sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));
  const allGroups = sortedGroups.map((g, index) => ({
    ...g,
    displayIndex: index + 1,
    color: getGroupColor(index),
  }));

  const currentGroupIndex = allGroups.findIndex((g) => g.name === groupName);
  const currentColor = currentGroupIndex >= 0 ? allGroups[currentGroupIndex].color : null;

  const updatePosition = useCallback(() => {
    if (buttonRef.current && showDropdown) {
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownMaxHeight = 350;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUpward = spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow;
      setDropdownPosition({ top: openUpward ? rect.top : rect.bottom, left: rect.left, width: rect.width, openUpward });
    }
  }, [showDropdown]);

  useEffect(() => {
    if (showDropdown) {
      updatePosition();
      const handleScroll = () => updatePosition();
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleScroll);
      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleScroll);
      };
    }
  }, [showDropdown, updatePosition]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const dd = dropdownRef.current;
      if (buttonRef.current && !buttonRef.current.contains(event.target) && (!dd || !dd.contains(event.target))) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const items = [{ id: '', name: 'Все группы', displayIndex: null, color: null }, ...allGroups];

  const handleSelect = (item) => {
    setShowDropdown(false);
    setHoveredIndex(-1);
    if (isInteractive) onFilter(item.id ? String(item.id) : '');
  };

  const handleKeyDown = (e) => {
    if (!isInteractive) return;
    if (!showDropdown) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowDropdown(true); setHoveredIndex(-1); }
      return;
    }
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); setHoveredIndex((p) => (p < items.length - 1 ? p + 1 : 0)); break;
      case 'ArrowUp': e.preventDefault(); setHoveredIndex((p) => (p > 0 ? p - 1 : items.length - 1)); break;
      case 'Enter': case ' ': e.preventDefault(); if (hoveredIndex >= 0) handleSelect(items[hoveredIndex]); break;
      case 'Escape': e.preventDefault(); setShowDropdown(false); setHoveredIndex(-1); break;
      default: break;
    }
  };

  const availableHeight = dropdownPosition.openUpward
    ? dropdownPosition.top - 8
    : window.innerHeight - dropdownPosition.top - 8;
  const actualMaxHeight = Math.min(350, availableHeight);

  if (!isInteractive) {
    return (
      <div style={{ position: 'relative', width: '100%' }}>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          {currentColor && (
            <div style={{
              position: 'absolute', left: '8px', width: '24px', height: '24px', borderRadius: '6px',
              backgroundColor: currentColor.bg, border: `2px solid ${currentColor.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '11px', fontWeight: '700', color: currentColor.text, zIndex: 1, pointerEvents: 'none',
            }}>
              {currentGroupIndex + 1}
            </div>
          )}
          <div
            style={{
              width: '100%',
              padding: currentColor ? '6px 8px 6px 40px' : '6px 8px',
              fontSize: '14px',
              border: currentColor ? `1px solid ${currentColor.border}` : '1px solid transparent',
              borderRadius: '6px',
              backgroundColor: currentColor ? currentColor.bg : 'transparent',
              color: currentColor ? currentColor.text : groupName ? 'inherit' : 'var(--text-muted)',
              fontWeight: groupName ? '500' : 'normal',
              textAlign: 'left',
            }}
          >
            {groupName || '—'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        {currentColor && (
          <div style={{
            position: 'absolute', left: '8px', width: '24px', height: '24px', borderRadius: '6px',
            backgroundColor: currentColor.bg, border: `2px solid ${currentColor.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '11px', fontWeight: '700', color: currentColor.text, zIndex: 1, pointerEvents: 'none',
          }}>
            {currentGroupIndex + 1}
          </div>
        )}
        <button
          ref={buttonRef}
          type="button"
          onClick={() => { setShowDropdown((p) => !p); setHoveredIndex(-1); }}
          onKeyDown={handleKeyDown}
          style={{
            width: '100%',
            padding: currentColor ? '6px 28px 6px 40px' : '6px 28px 6px 8px',
            fontSize: '14px', cursor: 'pointer',
            border: currentColor ? `1px solid ${currentColor.border}` : '1px solid #ddd',
            borderRadius: '6px',
            backgroundColor: currentColor ? currentColor.bg : 'white',
            color: currentColor ? currentColor.text : groupName ? 'inherit' : 'var(--text-muted)',
            fontWeight: groupName ? '500' : 'normal',
            textAlign: 'left', transition: 'all 0.2s ease', outline: 'none',
          }}
        >
          {groupName || '—'}
        </button>
        <div style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#999', fontSize: '10px' }}>
          ▼
        </div>
      </div>

      {showDropdown && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: 'fixed',
            ...(dropdownPosition.openUpward
              ? { bottom: window.innerHeight - dropdownPosition.top + 4 }
              : { top: dropdownPosition.top + 4 }),
            left: dropdownPosition.left,
            width: Math.max(dropdownPosition.width, 320),
            zIndex: 'var(--z-dropdown)', backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
            maxHeight: `${actualMaxHeight}px`, overflowY: 'auto', overflowX: 'hidden',
            boxShadow: dropdownPosition.openUpward ? '0 -8px 24px rgba(0,0,0,0.15)' : '0 8px 24px rgba(0,0,0,0.15)',
            borderRadius: '10px',
          }}
          onMouseDown={() => { isMouseDownOnDropdown.current = true; }}
          onMouseUp={() => { isMouseDownOnDropdown.current = false; }}
        >
          <div style={{
            padding: '12px 16px 8px', fontSize: '11px', fontWeight: '600', color: 'var(--text-muted)',
            textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid var(--border-color)',
            backgroundColor: 'var(--bg-primary)', position: 'sticky', top: 0, zIndex: 1,
          }}>
            Группы этапов ({allGroups.length})
          </div>

          {items.map((item, index) => {
            const isActive = isFilterActive && item.id && String(item.id) === String(isFilterActive);
            const isAllAndNoFilter = !item.id && !isFilterActive;
            const isHighlighted = isActive || isAllAndNoFilter;
            const isHovered = index === hoveredIndex;
            const itemColor = item.color;

            return (
              <div
                key={item.id || 'all'}
                onClick={() => handleSelect(item)}
                onMouseEnter={() => setHoveredIndex(index)}
                style={{
                  padding: '10px 16px', cursor: 'pointer',
                  backgroundColor: isHovered && itemColor ? itemColor.bg : isHovered ? '#e3f2fd' : isHighlighted ? '#e8f5e9' : 'white',
                  borderLeft: isHovered && itemColor ? `4px solid ${itemColor.border}` : isHovered ? '4px solid #2196f3' : isHighlighted ? '4px solid #4caf50' : '4px solid transparent',
                  transition: 'all 0.15s ease', userSelect: 'none',
                  display: 'flex', alignItems: 'center', gap: '12px',
                }}
              >
                {item.displayIndex != null ? (
                  <div style={{
                    width: '32px', height: '32px', borderRadius: '8px',
                    backgroundColor: itemColor.bg, border: `2px solid ${itemColor.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '13px', fontWeight: '700', color: itemColor.text, flexShrink: 0,
                  }}>
                    {item.displayIndex}
                  </div>
                ) : (
                  <div style={{
                    width: '32px', height: '32px', borderRadius: '8px',
                    backgroundColor: '#f5f5f5', border: '2px solid #e0e0e0',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '12px', color: '#999', flexShrink: 0,
                  }}>
                    ∗
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontWeight: isHighlighted ? '600' : '400', fontSize: '14px',
                    color: isHovered && itemColor ? itemColor.text : '#333', lineHeight: '1.3',
                  }}>
                    {item.name}
                  </div>
                </div>
                {isHighlighted && (
                  <span style={{ color: '#4caf50', fontSize: '14px', fontWeight: 'bold' }}>✓</span>
                )}
              </div>
            );
          })}

          <div style={{
            padding: '8px 16px', fontSize: '11px', color: '#999',
            borderTop: '1px solid #f0f0f0', backgroundColor: '#fafafa',
            display: 'flex', justifyContent: 'space-between', position: 'sticky', bottom: 0,
          }}>
            <span>↑↓ навигация</span>
            <span>Enter для выбора</span>
            <span>Esc для отмены</span>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default StageGroupSelect;
